import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { acceptAmount, acceptWhole } from "../lib/money";
import { Link, useBlocker, useNavigate, useParams } from "react-router";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Textarea,
  buttonStyles,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import {
  EMPTY_MODEL_FORM,
  FALLBACK_PUBLISHERS,
  costMicros,
  editFormFor,
  formatPriceLine,
  marginBps,
  parseRate,
  percent,
  sellMicros,
  setFormAvailability,
  toRate,
} from "../lib/aiModelForm";
import { useAiModel, useAiModels, useCreateAiModel, useUpdateAiModel } from "../lib/queries";
import type { AiModel, AiModelInput } from "../lib/types";

/**
 * Add or edit one catalogue model, on a route of its own.
 *
 * This was a modal, and it stopped fitting what it holds: twenty-odd fields across
 * four groups, including the two rate inputs that decide what every customer is
 * charged and a margin readout beneath them. As a dialog that was taller than a
 * laptop viewport, scrolling inside a scrolling page, with the figure you were
 * checking off screen while you typed the number that changes it.
 *
 * The layout here is built around fixing exactly that. The form runs down the
 * left in four labelled cards; the pricing readout sits in a sticky column beside
 * it and never leaves the screen. Typing a rate moves a number you can still see,
 * which is the whole reason this stopped being a dialog.
 *
 * A route also brought back a hazard a dialog does not have — the nav rail, the
 * back button and a stray link can all leave a half-typed rate behind. Hence the
 * guard below.
 */
export function AiModelFormPage() {
  const params = useParams();
  const navigate = useNavigate();

  /*
   * A splat, not a `:modelId`. A public id carries a slash —
   * `meta/llama-3.2-1b-instruct` is two path segments — and percent-encoding it
   * into one is a fight with the browser, the router and any proxy between them.
   * The API took the same route with `:modelId{.+}`.
   */
  const modelId = params["*"] ? decodeURIComponent(params["*"]) : undefined;
  const editing = Boolean(modelId);

  const model = useAiModel(modelId);
  const createModel = useCreateAiModel();
  const updateModel = useUpdateAiModel();

  /*
   * Publishers for the create form's prefix list, read from the catalogue the way
   * the list page reads them. One row is enough — the response carries the
   * publisher set whatever the page size.
   */
  const catalogue = useAiModels({ limit: 1 });
  const publishers = catalogue.data?.publishers ?? [];
  // Mirrors `DEFAULT_INFERENCE_MARKUP_BPS` in the API, and is only reached
  // before the catalogue loads — the server's own figure wins the moment it
  // arrives, so a drift here shows as a preview that corrects itself.
  const defaultMarkup = catalogue.data?.defaultMarkupBps ?? 800;

  const [form, setForm] = useState<AiModelInput>(EMPTY_MODEL_FORM);
  const [baseline, setBaseline] = useState<AiModelInput>(EMPTY_MODEL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /*
   * The rate fields keep their own strings. Deriving them from the micros on
   * every keystroke would rewrite "0.4" to "0.400000" mid-type and throw the
   * caret to the end — the usual controlled-number-input problem, and worse here
   * because these numbers set what customers are charged.
   */
  const [inputRate, setInputRate] = useState("0.000000");
  const [outputRate, setOutputRate] = useState("0.000000");

  /*
   * Seeded once the model arrives, keyed on its id so moving between two editors
   * re-seeds. Not a `key` on the component, which would also throw away an
   * in-flight edit every time the query refetched in the background.
   */
  useEffect(() => {
    if (!model.data) return;
    const next = editFormFor(model.data);
    setForm(next);
    setBaseline(next);
    setInputRate(toRate(model.data.official.inputMicrosPerMTok));
    setOutputRate(toRate(model.data.official.outputMicrosPerMTok));
  }, [model.data?.id]);

  const dirty = useMemo(
    () => !saved && JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline, saved],
  );

  /*
   * The hazard the modal did not have. A dialog could only be dismissed by its
   * own controls; a route can be left by the nav rail, a breadcrumb, the back
   * button or a stray link — and the thing left behind is a rate somebody was
   * part-way through typing.
   *
   * `beforeunload` covers the tab closing; the blocker covers everything inside
   * the app. Both are wired to the same `dirty`, so there is one answer to "is
   * there anything to lose".
   */
  /*
   * A ref, not the `saved` state, and that is the whole fix.
   *
   * `submit` set `saved` and navigated in the same tick, intending the blocker to
   * see the save and let its own redirect through. React batches the state
   * update, so it does not: `navigate` runs while `dirty` is still true, the
   * blocker challenges our own navigation, and an operator who saved
   * successfully is asked whether they want to discard the changes that were just
   * written. Which is alarming, and wrong twice — the work is saved, and
   * "Discard" would not un-save it.
   *
   * A ref updates synchronously, so the blocker reads the new value in the same
   * tick the navigation is requested.
   */
  const savedRef = useRef(false);
  const blocker = useBlocker(() => dirty && !savedRef.current);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const effectiveMarkup = form.markupBps ?? defaultMarkup;
  /*
   * The cost basis, which the published rate is only a bound on. 10000 means
   * "priced from the list rate" and is what almost every model carries.
   */
  const costFactor = form.costFactorBps ?? 10_000;
  const previewInput = sellMicros(form.officialInputMicros ?? 0, effectiveMarkup, costFactor);
  const previewOutput = sellMicros(form.officialOutputMicros ?? 0, effectiveMarkup, costFactor);
  const margin = marginBps(form.officialInputMicros ?? 0, previewInput, costFactor);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    try {
      if (editing && modelId) {
        await updateModel.mutateAsync({ id: modelId, input: form });
      } else {
        await createModel.mutateAsync(form);
      }
      /*
       * The ref first and synchronously, so the blocker below sees it in this
       * tick. `saved` is set too because the `beforeunload` handler and the
       * disabled state read it through the normal render path.
       */
      savedRef.current = true;
      setSaved(true);
      navigate("/ai-models");
    } catch (cause) {
      /*
       * Kept on the page rather than navigating away. The API refuses to list a
       * model the meter cannot bill, or one with no rate recorded at all — the
       * operator needs the message beside the field it is about, with what they
       * typed still in it.
       */
      setError(cause instanceof ApiError ? cause.message : "Could not save the model.");
    }
  }

  if (editing && model.isLoading) return <LoadingBlock label="Loading model…" />;
  if (editing && model.isError) {
    return (
      <div className="space-y-4">
        <Alert>
          Could not load <span className="ident">{modelId}</span>. It may have been deleted.
        </Alert>
        <Link to="/ai-models" className={buttonStyles("secondary", "md")}>
          Back to the catalogue
        </Link>
      </div>
    );
  }

  const saving = createModel.isPending || updateModel.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        title={editing ? (model.data?.displayName ?? "Edit model") : "New model"}
        description={
          editing
            ? "A rate you type here holds until the next sync, which reverts it if the supplier publishes one."
            : "Two ids: the public one customers send, and the routing one sent to the gateway."
        }
        action={
          <Link to="/ai-models" className={buttonStyles("secondary", "md")}>
            Back to catalogue
          </Link>
        }
      />

      {/*
        Said before the form rather than discovered on save. A check constraint
        refuses to list anything the meter cannot bill, so an operator can
        otherwise set Listed to yes, fill in everything else, submit, and get a
        rejection about a decision made three fields ago.
      */}
      {model.data && !model.data.sellable && (
        <Alert tone="warning">
          <span className="font-medium">This model cannot be listed.</span>{" "}
          {model.data.unsellableReason} Everything else here is still editable.
        </Alert>
      )}

      <form onSubmit={submit}>
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0 space-y-4">
            {error && <Alert>{error}</Alert>}

            <Card>
              <CardHeader
                title="Identity"
                description="What this model is called, here and upstream."
              />
              <div className="space-y-4 p-5">
                {!editing && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Publisher"
                      htmlFor="model-provider"
                      hint="First segment of the routing id."
                    >
                      <Select
                        id="model-provider"
                        value={form.provider ?? "openai"}
                        onChange={(e) => setForm({ ...form, provider: e.target.value })}
                      >
                        {(publishers.length > 0
                          ? publishers.map((entry) => entry.provider)
                          : FALLBACK_PUBLISHERS
                        ).map((provider: string) => (
                          <option key={provider} value={provider}>
                            {provider}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label="Public id"
                      htmlFor="model-id"
                      hint="publisher/model, e.g. meta/llama-3.2-1b-instruct. Customers see this one."
                    >
                      <Input
                        id="model-id"
                        required
                        value={form.id ?? ""}
                        onChange={(e) => setForm({ ...form, id: e.target.value })}
                      />
                    </Field>
                  </div>
                )}

                {/*
                 * Editable after creation, unlike the public id. A provider
                 * renaming a model upstream is a routing correction; forcing it
                 * through delete-and-recreate would take the model off sale and
                 * orphan its usage history to fix a string.
                 */}
                <Field
                  label="Upstream id"
                  htmlFor="model-upstream-id"
                  hint="The exact string sent to the gateway. Must begin with the provider."
                >
                  <Input
                    id="model-upstream-id"
                    required
                    value={form.upstreamId ?? ""}
                    onChange={(e) => setForm({ ...form, upstreamId: e.target.value })}
                  />
                </Field>

                {/*
                  No modality control, deliberately, and it is not simply tidying.
                  This form submits every field it holds, so the select that stood
                  here sent `chat` on every save whether anybody chose it or not —
                  one of the two things that stored 41 rows as chat while the same
                  sync wrote them a category of embedding, image or speech. A row
                  claiming chat satisfies `isSellable`, so those rows could be
                  listed and would then be routed to an endpoint that cannot serve
                  them. The supplier states what a model is, on every scan; a
                  hand-added row takes the default and can be corrected in the
                  database if one ever needs to be something else.
                */}
                <Field label="Display name" htmlFor="model-name">
                  <Input
                    id="model-name"
                    required
                    value={form.displayName ?? ""}
                    onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                  />
                </Field>

                <Field
                  label="Description"
                  htmlFor="model-description"
                  hint="Shown in the catalogue."
                >
                  <Textarea
                    id="model-description"
                    rows={2}
                    value={form.description ?? ""}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </Field>
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Pricing"
                description="What the provider charges, and what the platform adds to it."
              />
              <div className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Input per million tokens" htmlFor="model-input-rate">
                    <Input
                      id="model-input-rate"
                      inputMode="decimal"
                      required
                      value={inputRate}
                      onChange={(e) => {
                        setInputRate(e.target.value);
                        const micros = parseRate(e.target.value);
                        if (micros !== null) setForm({ ...form, officialInputMicros: micros });
                      }}
                      onBlur={() => setInputRate(toRate(form.officialInputMicros ?? 0))}
                    />
                  </Field>
                  <Field
                    label="Output per million tokens"
                    htmlFor="model-output-rate"
                    hint="Zero for embeddings — they produce no output tokens."
                  >
                    <Input
                      id="model-output-rate"
                      inputMode="decimal"
                      required
                      value={outputRate}
                      onChange={(e) => {
                        setOutputRate(e.target.value);
                        const micros = parseRate(e.target.value);
                        if (micros !== null) setForm({ ...form, officialOutputMicros: micros });
                      }}
                      onBlur={() => setOutputRate(toRate(form.officialOutputMicros ?? 0))}
                    />
                  </Field>
                </div>

                {/* No longer a gate on listing. A sync dates the rate it read, and
                    an operator can date one they typed — but nothing here blocks a
                    model whose date is blank, so the hint says what the field is
                    for rather than implying a requirement that no longer exists. */}
                <Field
                  label="Rate verified on"
                  htmlFor="model-verified"
                  hint="The day someone read the provider's rate card. Blank is fine — a sync fills it in."
                >
                  <Input
                    id="model-verified"
                    type="date"
                    value={form.officialVerifiedAt ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, officialVerifiedAt: e.target.value || null })
                    }
                  />
                </Field>

                <Field
                  label="Markup (basis points over cost)"
                  htmlFor="model-markup"
                  hint={`Empty follows the platform default of ${percent(defaultMarkup)}. 100bp is 1%.`}
                >
                  {/*
                    `inputMode` rather than `type="number"`. A scroll over a
                    focused number field changes it with no click, and this one
                    sets what every customer pays for this model.
                  */}
                  <Input
                    id="model-markup"
                    inputMode="numeric"
                    placeholder={String(defaultMarkup)}
                    value={form.markupBps ?? ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        // Empty returns the model to the platform default, which
                        // an operator must be able to get back to.
                        markupBps:
                          e.target.value === "" ? null : Number(acceptWhole(e.target.value) ?? form.markupBps ?? ""),
                      })
                    }
                  />
                </Field>

                {/*
                  The cost basis, and the field most likely to be misread as a
                  second markup. It is not: the markup above says what to earn,
                  this says what the model costs before earning anything.

                  The supplier's published rate is a ceiling rather than a bill —
                  measured over live traffic one model costs 48% of what it lists
                  at and another exactly 100%. Recording the real figure lowers
                  what the customer pays and leaves the margin untouched, which
                  is the opposite of what moving the markup does.

                  Defaults to 100% everywhere and should only be moved off it
                  with a measurement in hand: Admin → AI models shows realised
                  margin per model, which is where that number comes from. Too
                  low and the model sells under cost, so the hint says which way
                  is dangerous rather than only what the field means.
                */}
                {/*
                  Shown above the field rather than as a hint under it, because
                  it is a reason to change the value rather than an explanation
                  of what the value means — and because a hint is where an
                  operator's eye stops going once they have read it twice.
                */}
                {model.data?.costFactorStale && (
                  <Alert tone="warning">
                    The provider changed this model&rsquo;s published rate after this cost basis
                    was set{" "}
                    {model.data.costFactorSetAt
                      ? `on ${model.data.costFactorSetAt.slice(0, 10)}`
                      : ""}
                    . The basis is a share of that rate, so it now points at a figure that no
                    longer exists and the model is selling at a price nobody chose. Check the
                    measured margin on AI usage and set it again, or put it back to 100% to price
                    from the list rate.
                  </Alert>
                )}

                <Field
                  label="Cost basis (% of the provider's list rate)"
                  htmlFor="model-cost-factor"
                  hint="100% uses the published rate. Lower it only to a figure measured on AI usage. It also caps which endpoints may serve the model — too low refuses requests rather than selling below cost."
                >
                  {/*
                    Same reasoning, and the stakes are named in the hint above:
                    too low sells below cost, which is the alarm
                    `inference_billed_under_cost` exists to raise.
                  */}
                  <Input
                    id="model-cost-factor"
                    inputMode="decimal"
                    value={((form.costFactorBps ?? 10_000) / 100).toString()}
                    onChange={(e) => {
                      const accepted = acceptAmount(e.target.value);
                      if (accepted === null) return;
                      const percentOfList = Number(accepted);
                      setForm({
                        ...form,
                        /*
                         * Blank and zero both fall back to 100% rather than to
                         * nothing. A cost basis of zero would price the model at
                         * nothing, and an empty field is somebody mid-edit — not
                         * a decision to give the model away.
                         */
                        costFactorBps:
                          e.target.value === "" || !Number.isFinite(percentOfList) || percentOfList <= 0
                            ? 10_000
                            : Math.round(percentOfList * 100),
                      });
                    }}
                  />
                </Field>
              </div>
            </Card>

            <Card>
              <CardHeader title="Capability" description="What a caller can ask of it." />
              <div className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Context window"
                    htmlFor="model-context"
                    hint="Tokens. Blank if unstated."
                  >
                    <Input
                      id="model-context"
                      type="number"
                      min={1}
                      value={form.contextWindow ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          contextWindow: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                  <Field label="Max output tokens" htmlFor="model-max-output">
                    <Input
                      id="model-max-output"
                      type="number"
                      min={1}
                      value={form.maxOutputTokens ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          maxOutputTokens: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                </div>

                {/*
                  Sync-owned for anything that came from OpenRouter: the daily
                  scan reads vision, tools, structured output and reasoning off
                  the supplier's record and rewrites this list, so an edit here
                  lasts until the next scan. The field stays because a hand-added
                  partner model has no scan and needs somebody to say what it does.
                */}
                <Field
                  label="Capabilities"
                  htmlFor="model-capabilities"
                  hint="Rewritten by the daily sync from the supplier's record for OpenRouter models. For a hand-added model, comma separated: chat, vision, tools, structured-output, reasoning, embedding."
                >
                  <Input
                    id="model-capabilities"
                    value={(form.capabilities ?? []).join(", ")}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        capabilities: e.target.value
                          .split(",")
                          .map((entry) => entry.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </Field>
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Listing"
                description="Whether customers can see it, and where it sits."
              />
              <div className="space-y-4 p-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Listed" htmlFor="model-available">
                    <Select
                      id="model-available"
                      value={form.available ? "yes" : "no"}
                      onChange={(e) =>
                        setForm(setFormAvailability(form, e.target.value === "yes"))
                      }
                    >
                      <option value="no">Unlisted — hidden from the catalogue</option>
                      <option value="yes">Listed — customers can call it</option>
                    </Select>
                  </Field>
                  <Field
                    label="Zero data retention"
                    htmlFor="model-zdr"
                    hint="Honoured for OpenAI and Anthropic only."
                  >
                    <Select
                      id="model-zdr"
                      value={form.zdr ? "yes" : "no"}
                      onChange={(e) => setForm({ ...form, zdr: e.target.value === "yes" })}
                    >
                      <option value="no">Not available</option>
                      <option value="yes">Can be routed without retention</option>
                    </Select>
                  </Field>
                </div>

                {form.available && (
                  <Field
                    label="Featured position"
                    htmlFor="model-featured"
                    hint="Blank keeps this model out of the featured set."
                  >
                    <Input
                      id="model-featured"
                      type="number"
                      min={1}
                      value={form.featuredRank ?? ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          featuredRank: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                )}
              </div>
            </Card>
          </div>

          {/*
            Sticky, and that is the point of the whole layout. The rate inputs are
            on the left; this is what they do. In the dialog these two facts could
            not be on screen at once on a laptop, so an operator typed a price and
            scrolled to find out what it meant.
          */}
          <aside className="lg:sticky lg:top-6 space-y-4">
            {editing && model.data && <ModelFacts model={model.data} />}

            {/*
              The whole derivation is meaningless for a cost-priced model, so it
              is not drawn for one.
              
              official → cost → sell reads off a rate card, and these models have
              none: the images and videos catalogues publish no per-token rates,
              so every figure in this panel would be zero and the margin beside
              them 0.0%. Three zeros arranged as an arithmetic an operator is
              meant to trust is worse than saying there is no arithmetic.
              
              The markup still applies and is still theirs to set — it is just
              applied to what the request cost rather than to a published rate.
            */}
            {model.data?.billingMode === "upstream_cost" ? (
              <Card>
                <div className="space-y-3 p-5">
                  <p className="micro">Pricing</p>
                  <p className="text-sm leading-6 text-fg">Billed at cost plus markup</p>
                  {/*
                    Two reasons a model lands here and they are different
                    sentences, so the panel says which. A generation model has no
                    per-token rate at all. A multimodal embedding model has
                    several — one per kind of input, against a single token count
                    that cannot be split — which is a rate card the meter cannot
                    use rather than an absent one. Telling an operator the
                    provider "publishes no per-token rate" about a model whose
                    three rates are listed directly below would read as a bug.
                  */}
                  <p className="text-sm leading-6 text-fg-muted">
                    {model.data.category === "embedding"
                      ? "This model charges a different per-token rate for each kind of input, and a response reports one token count covering all of them — so no single rate card can bill it."
                      : "The provider publishes no per-token rate for this model, so there is no rate card to derive a price from."}{" "}
                    Each request is charged at what the provider charged us, plus this
                    model&rsquo;s markup of{" "}
                    <span className="text-fg">{percent(effectiveMarkup)}</span>.
                  </p>

                  {/*
                    What the provider does publish, which is not nothing.

                    This panel said there was no figure to price against and left
                    it there, which was true of the rate card and false of the
                    model: OpenRouter publishes a price per image, per second or
                    per megapixel, on a sub-resource for images and under
                    `pricing_skus` for videos, and the sync now reads both. The
                    list column learned to show them first; an operator choosing a
                    markup is the person who needs them most, and this is the page
                    where that choice is made.

                    Display only, and said so. The charge comes from what the
                    request reported, which already accounts for every axis below
                    — these are what that charge is made of, not a rate anything
                    multiplies.
                  */}
                  {(model.data.priceLines ?? []).length > 0 && (
                    <div className="space-y-1.5 border-t border-line pt-3">
                      <p className="micro">The provider&rsquo;s own prices</p>
                      <dl className="space-y-1">
                        {(model.data.priceLines ?? []).map((line) => (
                          <div
                            key={`${line.billable}/${line.unit}`}
                            className="flex items-baseline justify-between gap-3"
                          >
                            <dt className="text-xs text-fg-subtle">
                              {line.billable.replace(/_/g, " ")}
                            </dt>
                            <dd className="font-mono text-xs tabular-nums text-fg">
                              {formatPriceLine(line, model.data!.currency)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      <p className="text-xs leading-5 text-fg-subtle">
                        Before our markup, and nothing multiplies these. The charge is what the
                        request reported, which already covers every line here.
                      </p>
                    </div>
                  )}

                  <p className="text-xs leading-5 text-fg-subtle">
                    The rate fields below do not affect what a customer pays for this model.
                  </p>
                </div>
              </Card>
            ) : (
              <PricePreview
                officialInput={form.officialInputMicros ?? 0}
                officialOutput={form.officialOutputMicros ?? 0}
                sellInput={previewInput}
                sellOutput={previewOutput}
                margin={margin}
                markupBps={effectiveMarkup}
                costFactorBps={costFactor}
                ownMarkup={form.markupBps !== null && form.markupBps !== undefined}
                /*
                 * Read off the saved model rather than the form, because it is
                 * not an editable field: the cached rate is the supplier's, kept
                 * current by the sync. It is shown anyway — a markup is judged
                 * against the rate that actually bills the tokens, and on a
                 * caching client that is this one.
                 */
                officialCached={model.data?.official.cachedInputMicrosPerMTok ?? null}
                /*
                 * Read off the saved model for the same reason: the supplier
                 * sets it and the sync keeps it current. Shown for a sharper
                 * one, though — this is the rate that had been selling below
                 * cost, so an operator judging a markup needs to see that the
                 * markup now applies to it too.
                 */
                officialCacheWrite={model.data?.official.cacheWriteMicrosPerMTok ?? null}
              />
            )}
          </aside>
        </div>

        {/*
          Sticky, because the form is four cards long and the save button used to
          be at the bottom of all of them. It also carries the unsaved-state
          signal, which is the honest place for it: beside the control that
          resolves it.
        */}
        <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line bg-canvas/95 py-3 backdrop-blur">
          <p className="text-xs text-fg-subtle">
            {dirty ? "Unsaved changes" : editing ? "No changes yet" : "Nothing saved yet"}
          </p>
          <div className="flex gap-2">
            <Link to="/ai-models" className={buttonStyles("secondary", "md")}>
              Cancel
            </Link>
            <Button type="submit" loading={saving} disabled={editing && !dirty}>
              {editing ? "Save changes" : "Create model"}
            </Button>
          </div>
        </div>
      </form>

      <Modal
        open={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
        title="Leave without saving?"
        description="The rate and markup you typed will be lost. Nothing has been sent to the API."
      >
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => blocker.reset?.()}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={() => blocker.proceed?.()}>
            Discard changes
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/**
 * What this model is, as opposed to what it is being changed to.
 *
 * The fields an operator wants while editing and cannot type: which id the
 * catalogue keys on, where the rate came from, and when somebody last claimed to
 * have read it.
 *
 * "Rate source" used to read "Manual — syncs skip it", which was the opposite of
 * what happens. The sync matches on the routing id across every row, not only the
 * ones it wrote, so any model the supplier still publishes has its rate and its
 * source overwritten on the next scan whatever this says. That is deliberate —
 * tracking the published figure is the whole reason the sell price is derived
 * rather than stored — but an operator who read the old label would type a
 * correction, believe they had pinned it, and find it reverted within a day.
 *
 * Manual therefore means "last set by hand", not "protected". It only holds for a
 * model the supplier does not carry, which is the one case the sync never sees.
 */
function ModelFacts({ model }: { model: AiModel }) {
  const facts: [string, string][] = [
    ["Public id", model.id],
    ["Publisher", model.publisher],
    [
      "Rate source",
      model.officialSource === "manual" ? "Set by hand — a sync overwrites it" : "Synced",
    ],
    ["Verified", model.officialVerifiedAt ?? "Never"],
    ["Billing unit", model.billingUnit],
    /*
     * Both dates, because they answer different questions and an operator hits
     * both. "Published" is the supplier's own release date and what the Newest
     * sort orders by; "Discovered" is when a scan first brought the row in, which
     * is the one that explains why something turned up in the list this morning.
     *
     * Published reads "Not published" rather than "Never" when it is null: the
     * supplier did not state one, which is a fact about them, where "Never" would
     * read as something nobody has got round to.
     */
    ["Published", model.upstreamCreatedAt?.slice(0, 10) ?? "Not published"],
    ["Discovered", model.catalogueDiscoveredAt.slice(0, 10)],
  ];

  return (
    <Card>
      <CardHeader title="This model" />
      <dl className="divide-y divide-line text-xs">
        {facts.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3 px-5 py-2.5">
            <dt className="shrink-0 text-fg-subtle">{label}</dt>
            <dd className="ident min-w-0 truncate text-right text-fg">{value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/**
 * The three price levels, as one readout that moves while you type.
 *
 * Spelled out rather than summarised because the middle step is the one people
 * forget: the supplier's 5.5% credit fee sits between what the provider charges
 * and what the platform paid, so selling at the official rate reads as breaking
 * even and is a 5.2% loss on every call.
 *
 * The margin is the figure this page exists to get right, so it is the largest
 * thing in the panel and the only one that changes colour. Thin is 5% — the same
 * threshold the catalogue's margin column uses, because two warnings that
 * disagree about "thin" teach an operator to trust neither.
 */
function PricePreview({
  officialInput,
  officialOutput,
  sellInput,
  sellOutput,
  margin,
  markupBps,
  ownMarkup,
  costFactorBps,
  officialCached,
  officialCacheWrite,
}: {
  officialInput: number;
  officialOutput: number;
  sellInput: number;
  sellOutput: number;
  margin: number;
  markupBps: number;
  /** What fraction of the published rate this model costs. 10000 is 1.00x. */
  costFactorBps: number;
  ownMarkup: boolean;
  /** The supplier's cached-input rate. Null when they publish none. */
  officialCached: number | null;
  /**
   * The supplier's cache-write rate. Null when they publish none.
   *
   * Usually above `officialInput` rather than below it, which is what makes it
   * worth showing separately: it is the one rate here that raises a bill.
   */
  officialCacheWrite: number | null;
}) {
  const rows: [string, string, string][] = [
    ["Provider lists", toRate(officialInput), toRate(officialOutput)],
    [
      costFactorBps === 10_000 ? "Costs us" : `Costs us (${percent(costFactorBps)} of list)`,
      toRate(costMicros(officialInput, costFactorBps)),
      toRate(costMicros(officialOutput, costFactorBps)),
    ],
    ["Customer pays", toRate(sellInput), toRate(sellOutput)],
  ];

  return (
    <Card>
      <CardHeader title="Per million tokens" description="In / out, as you type." />

      <dl className="divide-y divide-line">
        {rows.map(([label, input, output], index) => (
          <div key={label} className="px-5 py-2.5">
            <dt className="text-xs text-fg-subtle">{label}</dt>
            <dd
              className={cx(
                "ident mt-0.5 text-sm tabular-nums",
                // The last row is what the customer is billed, so it is the one
                // at full contrast. The two above it are working.
                index === rows.length - 1 ? "text-fg" : "text-fg-muted",
              )}
            >
              {input} / {output}
            </dd>
          </div>
        ))}
      </dl>

      {/*
        The third rate, and the one the two columns above cannot hold.
        
        Not editable, unlike the pair being typed above it: this is the
        supplier's figure, kept current by the sync. It is here because a markup
        set against the input rate is being applied to this rate too, and on a
        client that caches — which is what this endpoint is sold to — it is the
        rate billing most of the tokens.

        Absent entirely when the provider publishes none, rather than shown as a
        zero or a dash. A model without a cached rate bills hits at the plain
        input rate, and a row saying "—" invites the reading that hits are free.
      */}
      {officialCached !== null && (
        <div className="border-t border-line px-5 py-2.5">
          <dt className="text-xs text-fg-subtle">Cache hit, in</dt>
          <dd className="ident mt-0.5 text-sm tabular-nums text-fg-muted">
            {toRate(officialCached)} provider ·{" "}
            {toRate(costMicros(officialCached, costFactorBps))} cost ·{" "}
            <span className="text-fg">
              {toRate(sellMicros(officialCached, markupBps, costFactorBps))} customer
            </span>
          </dd>
        </div>
      )}

      {/*
        The fourth rate, on the same terms as the third — and the only one on this
        card that can sit *above* the input rate rather than below it.

        Labelled with the direction rather than left for an operator to compare
        two numbers across two rows. A write costs about 1.25x a plain input token
        on Anthropic and OpenAI models, so "dearer" is the ordinary answer here
        and reads as a bug to anyone who assumes every cache rate is a discount.
        Several Google models publish one below the input rate, so the comparison
        is computed rather than assumed.

        Absent when the provider publishes none, like the row above and for the
        same reason: those models bill writes at the plain input rate, and a dash
        invites the reading that writes are free.
      */}
      {officialCacheWrite !== null && (
        <div className="border-t border-line px-5 py-2.5">
          <dt className="text-xs text-fg-subtle">
            Cache write, in{" "}
            <span className={officialCacheWrite > officialInput ? "text-warn" : "text-fg-subtle"}>
              · {officialCacheWrite > officialInput ? "dearer" : "cheaper"} than input
            </span>
          </dt>
          <dd className="ident mt-0.5 text-sm tabular-nums text-fg-muted">
            {toRate(officialCacheWrite)} provider ·{" "}
            {toRate(costMicros(officialCacheWrite, costFactorBps))} cost ·{" "}
            <span className="text-fg">
              {toRate(sellMicros(officialCacheWrite, markupBps, costFactorBps))} customer
            </span>
          </dd>
        </div>
      )}

      <div className="border-t border-line px-5 py-4">
        <p className="text-xs text-fg-subtle">Margin over cost</p>
        <p
          className={cx(
            "mt-1 text-2xl font-semibold tabular-nums",
            margin < 0 ? "text-danger" : margin < 500 ? "text-warn" : "text-fg",
          )}
        >
          {percent(margin)}
        </p>
        <p className="mt-1.5 text-xs leading-5 text-fg-subtle">
          {margin < 0
            ? "Below cost. Every call loses money."
            : margin < 500
              ? "Thin. The supplier's credit fee is already inside cost."
              : `${percent(markupBps)} markup${ownMarkup ? " on this model" : " from the platform default"}.`}
        </p>
      </div>
    </Card>
  );
}
